import type { OrdenCompraDetalle } from '../api/tipos';

/**
 * La orden de compra en papel.
 *
 * Se arma como un documento aparte y no imprimiendo la pantalla: lo que
 * se manda al proveedor no lleva botones, ni bandejas, ni el margen de
 * venta —que es información nuestra y no suya—. Es el mismo dato visto
 * como documento.
 *
 * Se abre en una ventana propia con su HTML dentro. Se podría generar el
 * PDF en el servidor, pero eso es una dependencia y un endpoint más para
 * algo que el navegador hace igual de bien: imprimir a papel o guardar a
 * PDF sale del mismo diálogo.
 */

const ETIQUETAS: Record<string, string> = {
  borrador: 'Borrador',
  pendiente_autorizacion: 'Pendiente de autorización',
  autorizada: 'Autorizada',
  rechazada: 'Rechazada',
  enviada_proveedor: 'Enviada al proveedor',
  recibida_parcial: 'Recibida parcialmente',
  recibida_total: 'Recibida',
  pagada: 'Pagada',
  cancelada: 'Cancelada',
};

function moneda(valor: number): string {
  return valor.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}

function fecha(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-MX', {
    day: '2-digit', month: 'long', year: 'numeric',
  });
}

/** El texto del usuario va dentro del HTML: hay que escaparlo. */
function esc(texto: string | null | undefined): string {
  if (texto === null || texto === undefined) return '';
  return String(texto)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function dato(etiqueta: string, valor: string | null | undefined): string {
  return `<p><span class="et">${esc(etiqueta)}</span>${esc(valor ?? '—')}</p>`;
}

export function documentoDeCompra(orden: OrdenCompraDetalle): string {
  const emisor = orden.emisor;
  const proveedor = orden.proveedorDatos;

  const partidas = orden.partidas.map((p, i) => `
    <tr>
      <td class="num">${i + 1}</td>
      <td>
        ${p.refaccion
    ? `<span class="mono">${esc(p.refaccion.codigo)}</span> ${esc(p.refaccion.nombre)}`
    : esc(p.descripcionLibre)}
      </td>
      <td class="num">${p.cantidad}</td>
      <td class="num">${moneda(p.montoCompra)}</td>
      <td class="num">${moneda(p.importe)}</td>
    </tr>`).join('');

  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>${esc(orden.folio)}</title>
<style>
  @page { size: letter; margin: 18mm 16mm; }
  body {
    font: 12px/1.5 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    color: #1a1a1a; margin: 0;
  }
  h1 { font-size: 20px; margin: 0 0 2px; }
  h2 { font-size: 13px; margin: 22px 0 6px; text-transform: uppercase; letter-spacing: .04em; }
  .cabecera { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px;
              border-bottom: 2px solid #1a1a1a; padding-bottom: 12px; }
  .cabecera .derecha { text-align: right; white-space: nowrap; }
  .folio { font-size: 22px; font-weight: 700; }
  .partes { display: flex; gap: 32px; margin-top: 18px; }
  .partes > div { flex: 1; }
  p { margin: 2px 0; }
  .et { display: inline-block; min-width: 96px; color: #555; }
  .mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
  table { width: 100%; border-collapse: collapse; margin-top: 6px; }
  th, td { padding: 6px 8px; border-bottom: 1px solid #ddd; text-align: left; vertical-align: top; }
  th { background: #f2f2f2; border-bottom: 1px solid #999; font-size: 11px;
       text-transform: uppercase; letter-spacing: .03em; }
  .num { text-align: right; white-space: nowrap; }
  .totales { margin-top: 10px; margin-left: auto; width: 260px; }
  .totales td { border: 0; padding: 3px 8px; }
  .totales .final td { border-top: 2px solid #1a1a1a; font-weight: 700; font-size: 14px; }
  .nota { margin-top: 14px; padding: 8px 10px; background: #f7f7f7; }
  .firmas { display: flex; gap: 48px; margin-top: 56px; }
  .firmas > div { flex: 1; border-top: 1px solid #1a1a1a; padding-top: 6px;
                  text-align: center; font-size: 11px; color: #555; }
  .pie { margin-top: 28px; font-size: 10px; color: #777; text-align: center; }
</style>
</head>
<body>
  <div class="cabecera">
    <div>
      <h1>${esc(emisor?.nombreComercial ?? emisor?.razonSocial ?? 'APRISA')}</h1>
      ${emisor?.nombreComercial ? `<p>${esc(emisor.razonSocial)}</p>` : ''}
      <p>RFC ${esc(emisor?.rfc)}</p>
      ${emisor?.direccion ? `<p>${esc(emisor.direccion)}${emisor.codigoPostal ? ` · CP ${esc(emisor.codigoPostal)}` : ''}</p>` : ''}
      ${emisor?.telefono || emisor?.correo
    ? `<p>${esc([emisor?.telefono, emisor?.correo].filter(Boolean).join(' · '))}</p>`
    : ''}
    </div>
    <div class="derecha">
      <p>Orden de compra</p>
      <p class="folio mono">${esc(orden.folio)}</p>
      <p>${fecha(orden.fechaEmision ?? orden.creadoEn)}</p>
      <p>${esc(ETIQUETAS[orden.estatus] ?? orden.estatus)}</p>
    </div>
  </div>

  <div class="partes">
    <div>
      <h2>Proveedor</h2>
      ${dato('Nombre', proveedor?.nombre ?? orden.proveedor?.nombre)}
      ${dato('RFC', proveedor?.rfc)}
      ${dato('Contacto', proveedor?.contacto)}
      ${dato('Teléfono', proveedor?.telefono)}
      ${dato('Correo', proveedor?.correo)}
      ${proveedor?.direccion ? dato('Dirección', proveedor.direccion) : ''}
    </div>
    <div>
      <h2>Condiciones</h2>
      ${dato('Entrega', fecha(orden.fechaEntregaEstimada))}
      ${dato('Crédito', proveedor?.diasCredito !== null && proveedor?.diasCredito !== undefined
    ? `${proveedor.diasCredito} días` : 'Por convenir')}
      ${dato('IVA', orden.aplicaIva ? 'Aplica' : 'No aplica')}
      ${orden.ordenTrabajo ? dato('Para la orden', orden.ordenTrabajo.folio) : ''}
    </div>
  </div>

  <h2>Partidas</h2>
  <table>
    <thead>
      <tr>
        <th style="width:28px">#</th>
        <th>Descripción</th>
        <th class="num" style="width:70px">Cantidad</th>
        <th class="num" style="width:100px">P. unitario</th>
        <th class="num" style="width:110px">Importe</th>
      </tr>
    </thead>
    <tbody>${partidas}</tbody>
  </table>

  <table class="totales">
    <tr><td>Subtotal</td><td class="num">${moneda(orden.subtotal)}</td></tr>
    ${orden.aplicaIva ? `<tr><td>IVA</td><td class="num">${moneda(orden.iva)}</td></tr>` : ''}
    ${orden.retencion > 0
    ? `<tr><td>Retención</td><td class="num">−${moneda(orden.retencion)}</td></tr>` : ''}
    <tr class="final"><td>Total</td><td class="num">${moneda(orden.total)}</td></tr>
  </table>

  ${orden.observaciones
    ? `<div class="nota"><strong>Observaciones:</strong> ${esc(orden.observaciones)}</div>` : ''}

  <div class="firmas">
    <div>Autorizó</div>
    <div>Proveedor — recibí de conformidad</div>
  </div>

  <p class="pie">
    Documento generado por APRISA · ${esc(orden.folio)} ·
    ${fecha(new Date().toISOString())}
  </p>
</body>
</html>`;
}

/**
 * Abre el documento en una ventana e invoca la impresión.
 *
 * La ventana se queda abierta después de imprimir: cerrarla sola compite
 * con el diálogo del navegador —en Safari lo cancela— y además impide
 * revisar el documento antes de mandarlo.
 */
export function imprimirCompra(orden: OrdenCompraDetalle): void {
  const ventana = window.open('', '_blank', 'width=900,height=1000');
  if (!ventana) {
    window.alert('El navegador bloqueó la ventana. Permite las ventanas emergentes de este sitio.');
    return;
  }

  ventana.document.write(documentoDeCompra(orden));
  ventana.document.close();
  ventana.focus();

  // Un respiro para que aplique los estilos antes de medir las páginas.
  ventana.setTimeout(() => ventana.print(), 250);
}
